/**
 * ErrorDisplay Component
 *
 * A generic, reusable component for displaying error messages for different
 * scenarios across various entity types (courses, classes, quests, etc.)
 * with appropriate actions and contact information.
 *
 * @module components/common/errors/ErrorDisplay
 */

import PropTypes from 'prop-types';
import { useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Link,
  useColorModeValue,
  Heading,
} from '@chakra-ui/react';
import { ArrowBackIcon, EmailIcon, RepeatIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';

/**
 * ErrorDisplay component for handling different error scenarios across entities
 *
 * @param {Object} props - Component props
 * @param {Object} props.error - Error object containing status, data, and details
 * @param {number} props.error.status - HTTP status code
 * @param {Object} props.error.data - Error response data
 * @param {string} props.error.data.type - RFC error type URL
 * @param {string} props.error.data.title - Error title
 * @param {string} props.error.data.detail - Error description
 * @param {string[]} props.error.data.emails - Admin email addresses for contact
 * @param {number[]} props.error.data.classIds - Available class IDs for access
 * @param {Object} props.entityConfig - Configuration for the specific entity type
 * @param {string} props.entityConfig.name - Entity name (e.g., "Course", "Class", "Quest")
 * @param {string} props.entityConfig.homeRoute - Route to navigate back to (default: "/")
 * @param {number} [props.entityConfig.courseId] - Optional course ID for enhanced class access links
 * @param {string} props.previousUrl - The URL that the user was trying to access before the error
 * @returns {JSX.Element}
 */
function ErrorDisplay({ error, entityConfig = {}, previousUrl = null }) {
  const navigate = useNavigate();

  // ALL useColorModeValue hooks must be called before any conditional returns
  const metadataBgColor = useColorModeValue('gray.50', 'gray.700');

  // Default entity configuration
  const { name = 'Resource', homeRoute = '/' } = entityConfig;

  // Extract error data for easier access
  const { status, data } = error || {};
  const { classIds } = data || {};

  /**
   * Generate appropriate class access link with course ID if available from entityConfig
   * Memoized to prevent unnecessary re-renders in useEffect
   */
  const getClassAccessLink = useCallback(
    (classId) => {
      const { courseId } = entityConfig;
      return courseId
        ? `/classes/${classId}/courses/${courseId}`
        : `/classes/${classId}`;
    },
    [entityConfig]
  );

  /**
   * Auto-redirect when there's exactly one class link available
   * This improves user experience by eliminating unnecessary clicks
   * Uses enhanced class access link with course ID if available
   */
  useEffect(() => {
    // Only auto-redirect for 403 errors with exactly one class link
    if (status === 403 && classIds && classIds.length === 1) {
      const redirectRoute = getClassAccessLink(classIds[0]);

      // Use setTimeout to ensure the navigation happens after component mount
      const timeoutId = setTimeout(() => {
        navigate(redirectRoute);
      }, 100);

      // Cleanup timeout on component unmount
      return () => clearTimeout(timeoutId);
    }

    // Return undefined when no cleanup is needed (ESLint consistent-return requirement)
    return undefined;
  }, [status, classIds, navigate, entityConfig, getClassAccessLink]);

  // Default fallback for unknown errors
  if (!error || !error.status) {
    return (
      <Box textAlign="center" py={20} px={6}>
        <Heading
          display="inline-block"
          as="h2"
          size="2xl"
          bgGradient="linear(to-r, red.500, red.900)"
          backgroundClip="text"
        >
          ERROR
        </Heading>
        <Text fontSize="18px" fontWeight={500} mt={3} mb={2}>
          Error Loading {name}
        </Text>
        <Text color="gray.500" mb={6}>
          An unexpected error occurred while loading the {name.toLowerCase()}.
          Please try again or contact support if the problem persists.
        </Text>
        <Button
          leftIcon={<ArrowBackIcon />}
          colorScheme="blue"
          bgGradient="linear(to-r, blue.500, blue.900)"
          color="white"
          variant="solid"
          onClick={() => navigate(homeRoute)}
          title={homeRoute}
          _hover={{
            bgGradient: 'linear(to-r, blue.600, blue.800)',
            transform: 'translateY(-2px)',
            boxShadow: 'lg',
          }}
          _active={{
            transform: 'translateY(0)',
            boxShadow: 'md',
          }}
          transition="all 0.2s ease"
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  const { title, detail, emails } = data || {};

  /**
   * Get clean error title based on status and error data
   */
  const getErrorTitle = () => {
    if (title) return title;

    switch (status) {
      case 404:
        return `${name} Not Found`;
      case 403:
        return 'Access Forbidden';
      default:
        return `Error Loading ${name}`;
    }
  };

  /**
   * Get clean error description based on status and error data
   */
  const getErrorDescription = () => {
    // If detail exists and is different from title, use it
    if (detail && detail !== title) {
      return detail;
    }

    // If detail is same as title or doesn't exist, provide meaningful fallback descriptions
    switch (status) {
      case 404:
        return `The ${name.toLowerCase()} you're looking for doesn't exist or may have been removed. Please check the URL or contact your administrator if you believe this is an error.`;
      case 403:
        return `You don't have the necessary permissions to access this ${name.toLowerCase()}. Contact your administrator or check if you need to be enrolled in a specific class to gain access.`;
      default:
        return `An unexpected error occurred while loading the ${name.toLowerCase()}. Please try again later or contact support if the problem persists.`;
    }
  };

  /**
   * Get the appropriate gradient colors based on status code
   * Using theme color #d92e48 for consistency with sidebar
   */
  const getStatusGradient = () => {
    if (status === 404) return 'linear(to-r, #d92e48, #b91c3c)';
    if (status === 403) return 'linear(to-r, orange.500, orange.900)';
    return 'linear(to-r, #d92e48, #b91c3c)';
  };

  /**
   * Get the primary theme gradient for buttons
   */
  const getThemeGradient = () => {
    return 'linear(to-r, #d92e48, #b91c3c)';
  };

  /**
   * Get the theme hover gradient for buttons
   */
  const getThemeHoverGradient = () => {
    return 'linear(to-r, #c1284a, #991b1b)';
  };

  /**
   * Generate appropriate link text for class access
   */
  const getClassAccessText = (classId) => {
    const { courseId } = entityConfig;
    return courseId
      ? `Access Class #${classId} - Course #${courseId}`
      : `Access Class #${classId}`;
  };

  /**
   * Renders additional content for specific error types (contact info, class links)
   */
  const renderAdditionalContent = () => {
    if (status === 403) {
      // Show admin contacts
      if (emails && emails.length > 0) {
        return (
          <Box
            mt={8}
            mb={8}
            p={6}
            bg={metadataBgColor}
            borderRadius="lg"
            maxW="600px"
            mx="auto"
            border="1px solid"
            borderColor="gray.200"
            _dark={{ borderColor: 'gray.600' }}
          >
            <Text
              fontWeight="600"
              mb={5}
              fontSize="xl"
              color="gray.800"
              textAlign="center"
            >
              Need Help? Contact Administrators
            </Text>
            <VStack spacing={3}>
              {emails.map((email) => (
                <Link
                  key={email}
                  href={`mailto:${email}`}
                  color="blue.600"
                  fontSize="lg"
                  fontWeight="medium"
                  display="flex"
                  alignItems="center"
                  p={3}
                  borderRadius="md"
                  _hover={{
                    color: 'blue.800',
                    bg: 'blue.50',
                    textDecoration: 'none',
                    transform: 'translateY(-1px)',
                  }}
                  transition="all 0.2s ease"
                >
                  <EmailIcon mr={3} />
                  {email}
                </Link>
              ))}
            </VStack>
          </Box>
        );
      }

      // Show class access links
      if (classIds && classIds.length > 0) {
        return (
          <Box
            mt={8}
            mb={8}
            p={6}
            bg={metadataBgColor}
            borderRadius="lg"
            maxW="600px"
            mx="auto"
            border="1px solid"
            borderColor="gray.200"
            _dark={{ borderColor: 'gray.600' }}
          >
            <Text
              fontWeight="600"
              mb={5}
              fontSize="xl"
              color="gray.800"
              textAlign="center"
            >
              Available Access Options
            </Text>
            <VStack spacing={3}>
              {classIds.map((id) => {
                const accessLink = getClassAccessLink(id);
                return (
                  <Link
                    key={id}
                    href={accessLink}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(accessLink);
                    }}
                    color="blue.600"
                    fontSize="lg"
                    fontWeight="medium"
                    display="block"
                    w="full"
                    textAlign="center"
                    p={3}
                    borderRadius="md"
                    border="2px solid"
                    borderColor="blue.200"
                    _hover={{
                      color: 'blue.800',
                      bg: 'blue.50',
                      borderColor: 'blue.400',
                      textDecoration: 'none',
                      transform: 'translateY(-1px)',
                    }}
                    transition="all 0.2s ease"
                  >
                    {getClassAccessText(id)}
                  </Link>
                );
              })}
            </VStack>
          </Box>
        );
      }
    }
    return null;
  };

  return (
    <Box textAlign="center" minH="100vh" py={20} px={6}>
      {/* Status Code with gradient - improved styling */}
      <Heading
        display="inline-block"
        as="h2"
        size="2xl"
        bgGradient={getStatusGradient()}
        backgroundClip="text"
        mb={4}
      >
        {status}
      </Heading>

      {/* Error Title */}
      <Text fontSize="24px" fontWeight={600} mt={3} mb={4} color="gray.800">
        {getErrorTitle()}
      </Text>

      {/* Error Description */}
      <Text color="gray.500" mb={8} fontSize="lg" maxW="600px" mx="auto">
        {getErrorDescription()}
      </Text>

      {/* Additional Content (contacts, class links, etc.) */}
      {renderAdditionalContent()}

      {/* Action Buttons - horizontal layout with Home first, Try Again last */}
      <Box pt={6}>
        <HStack spacing={4} justify="center" wrap="wrap">
          {/* Home Button - outline design */}
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={() => navigate(homeRoute)}
            variant="outline"
            borderColor="#d92e48"
            color="#d92e48"
            size="lg"
            px={8}
            py={3}
            borderRadius="lg"
            title={homeRoute}
            _hover={{
              bg: '#d92e48',
              color: 'white',
              borderColor: '#d92e48',
              transform: 'translateY(-1px)',
              boxShadow: 'xl',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.2s ease"
            fontWeight="semibold"
          >
            Home
          </Button>

          {/* Try Again Button - only show if previousUrl is available */}
          {previousUrl && (
            <Button
              leftIcon={<RepeatIcon />}
              onClick={() => navigate(previousUrl)}
              bgGradient={getThemeGradient()}
              color="white"
              variant="solid"
              size="lg"
              px={8}
              py={3}
              borderRadius="lg"
              title={previousUrl}
              _hover={{
                bgGradient: getThemeHoverGradient(),
                transform: 'translateY(-1px)',
                boxShadow: 'xl',
              }}
              _active={{
                transform: 'translateY(0)',
              }}
              transition="all 0.2s ease"
              fontWeight="semibold"
            >
              Try Again
            </Button>
          )}
        </HStack>
      </Box>
    </Box>
  );
}

ErrorDisplay.propTypes = {
  error: PropTypes.shape({
    status: PropTypes.number,
    data: PropTypes.shape({
      type: PropTypes.string,
      title: PropTypes.string,
      detail: PropTypes.string,
      emails: PropTypes.arrayOf(PropTypes.string),
      classIds: PropTypes.arrayOf(PropTypes.number),
    }),
  }),
  entityConfig: PropTypes.shape({
    name: PropTypes.string,
    homeRoute: PropTypes.string,
    courseId: PropTypes.number,
  }),
  previousUrl: PropTypes.string,
};

ErrorDisplay.defaultProps = {
  error: null,
  entityConfig: {},
  previousUrl: null,
};

export default ErrorDisplay;
